import { create } from "zustand";

import { BagProgressed, TGTGBagAdditionMethod, TGTGBagProgressAction } from "analytics/events";
import { TranslatedProductWithPrice } from "shared/models/products/types";

export const GENERATED_SKU_PREFIX = "GEN-";
const PRICE_LIMIT_THRESHOLD = 10;

export type ProductsMap = Record<string, ProductWithQuantity>;
export type ProductWithQuantity = TranslatedProductWithPrice & {
  quantity: number;
};

/**
 * Outcome of a quantity change, so callers can surface the reason it was refused.
 * "rejected" covers unusable input (missing price/name, unknown sku).
 */
export type BagQuantityUpdateResult = "updated" | "limit-reached" | "rejected";

type TGTGBagStore = {
  bagId: string | null;
  bagKey: string | null;
  bagSku: string | null;
  minimumValue: number | null;
  maximumThreshold: number;
  maximumUnitsPerSku: number | null;
  minimumDistinctSkus: number | null;
  allowedCategories: string[];
  excludedCategories: string[];
  minimumExpirationDate: Date | null;
  totalPrice: number;
  productsMap: ProductsMap;
  products: string[];
  productToAdd: TranslatedProductWithPrice | null;
  showProductNotListedModal: boolean;
  showProductNotEligibleModal: boolean;
  notEligibleProductName: string | null;
  hasShownGuidelines: boolean;
};

type TrackBagProgress = (name: BagProgressed["name"], payload: BagProgressed["payload"]) => void;

type TGTGBagStoreWithMethods = TGTGBagStore & {
  setSelectedBag(
    bagId: string,
    bagKey: string | null,
    minimumValue: number | null,
    bagSku: string | null,
    maximumThreshold: number,
    maximumUnitsPerSku: number | null,
    minimumDistinctSkus: number | null,
    allowedCategories: string[],
    excludedCategories: string[],
  ): void;
  addProduct(
    newProduct: TranslatedProductWithPrice,
    method: TGTGBagAdditionMethod,
    trackBagProgress: TrackBagProgress,
  ): BagQuantityUpdateResult;
  setProductToAdd(product: TranslatedProductWithPrice | null): void;
  setShowProductNotListedModal: (isOpen: boolean) => void;
  setShowProductNotEligibleModal: (isOpen: boolean, productName?: string | null) => void;
  setHasShownGuidelines: () => void;
  increaseQuantity(sku: string, trackBagProgress: TrackBagProgress): BagQuantityUpdateResult;
  decreaseQuantity(sku: string, trackBagProgress: TrackBagProgress): void;
  reset: () => void;
};

const initialState: TGTGBagStore = {
  bagId: null,
  bagKey: null,
  bagSku: null,
  minimumValue: null,
  maximumThreshold: PRICE_LIMIT_THRESHOLD,
  maximumUnitsPerSku: null,
  minimumDistinctSkus: null,
  allowedCategories: [],
  excludedCategories: [],
  minimumExpirationDate: null,
  totalPrice: 0,
  productsMap: {},
  products: [],
  productToAdd: null,
  showProductNotListedModal: false,
  showProductNotEligibleModal: false,
  notEligibleProductName: null,
  hasShownGuidelines: false,
};

// Helper functions
const calculateTotalPrice = (currentTotal: number, change: number) =>
  Math.round((currentTotal + change) * 100) / 100;
const getValidSku = (sku: string) => (sku.startsWith(GENERATED_SKU_PREFIX) ? null : sku);
const isOverMaxUnitsPerSku = (newQuantity: number, maximumUnitsPerSku: number | null) =>
  maximumUnitsPerSku !== null && newQuantity > maximumUnitsPerSku;

const getMinimumExpirationDate = (bagKey: string | null): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (bagKey === "bakery") {
    today.setDate(today.getDate() - 1);
  } else if (bagKey === "xl") {
    today.setDate(today.getDate() + 1);
  }
  return today;
};

export const useTGTGBagStore = create<TGTGBagStoreWithMethods>()((set, get) => ({
  ...initialState,
  setSelectedBag: (
    bagId,
    bagKey,
    minimumValue,
    bagSku,
    maximumThreshold,
    maximumUnitsPerSku,
    minimumDistinctSkus,
    allowedCategories,
    excludedCategories,
  ) =>
    set({
      bagId,
      bagKey,
      minimumValue,
      bagSku,
      maximumThreshold: maximumThreshold ?? PRICE_LIMIT_THRESHOLD,
      maximumUnitsPerSku,
      minimumDistinctSkus,
      allowedCategories,
      excludedCategories,
      minimumExpirationDate: getMinimumExpirationDate(bagKey),
      hasShownGuidelines: false,
    }),
  addProduct: (newProduct, method, trackBagProgress) => {
    const state = get();
    const { productPrice, productName } = newProduct;
    if (!productPrice || !productName) return "rejected";
    const sku = newProduct.productSku;
    const existingProduct = state.productsMap[sku];
    const newQuantity = existingProduct ? existingProduct.quantity + 1 : 1;
    if (isOverMaxUnitsPerSku(newQuantity, state.maximumUnitsPerSku)) return "limit-reached";
    const updatedProductsMap = {
      ...state.productsMap,
      [sku]: {
        ...newProduct,
        quantity: newQuantity,
      },
    };
    const updatedProducts = existingProduct ? state.products : [...state.products, sku];
    const newTotalPrice = calculateTotalPrice(state.totalPrice, newProduct.productPrice ?? 0);
    set({
      productsMap: updatedProductsMap,
      products: updatedProducts,
      totalPrice: newTotalPrice,
    });
    trackBagProgress("bagProgressed", {
      action: existingProduct
        ? TGTGBagProgressAction.ProductQuantityUpdated
        : TGTGBagProgressAction.ProductAdded,
      bag_id: state.bagId!,
      product_sku: getValidSku(sku),
      ean: newProduct.ean,
      quantity: newQuantity,
      method: existingProduct ? null : method,
      product_name: productName,
      product_price: productPrice,
    });

    return "updated";
  },
  setProductToAdd: (product) => set({ productToAdd: product }),
  setShowProductNotListedModal: (isOpen) => set({ showProductNotListedModal: isOpen }),
  setShowProductNotEligibleModal: (isOpen, productName = null) =>
    set({ showProductNotEligibleModal: isOpen, notEligibleProductName: productName }),
  setHasShownGuidelines: () => set({ hasShownGuidelines: true }),
  increaseQuantity: (sku, trackBagProgress) => {
    const state = get();
    const product = state.productsMap[sku];
    if (!product) return "rejected";
    const newQuantity = product.quantity + 1;
    if (isOverMaxUnitsPerSku(newQuantity, state.maximumUnitsPerSku)) return "limit-reached";
    const newTotalPrice = calculateTotalPrice(state.totalPrice, product.productPrice ?? 0);
    const updatedProductsMap = { ...product, quantity: newQuantity };
    set({
      productsMap: { ...state.productsMap, [sku]: updatedProductsMap },
      totalPrice: newTotalPrice,
    });
    trackBagProgress("bagProgressed", {
      action: TGTGBagProgressAction.ProductQuantityUpdated,
      bag_id: state.bagId!,
      product_sku: getValidSku(sku),
      ean: product.ean,
      quantity: newQuantity,
      method: null,
      product_name: product.productName!,
      product_price: product.productPrice!,
    });
    return "updated";
  },
  decreaseQuantity: (sku, trackBagProgress) =>
    set((state) => {
      const product = state.productsMap[sku];
      if (!product) return state;
      const newTotalPrice = calculateTotalPrice(state.totalPrice, -(product.productPrice ?? 0));
      if (product.quantity === 1) {
        const { [sku]: removedProduct, ...updatedProductsMap } = state.productsMap;
        const updatedProducts = state.products.filter((id) => id !== sku);
        trackBagProgress("bagProgressed", {
          action: TGTGBagProgressAction.ProductRemoved,
          bag_id: state.bagId!,
          product_sku: getValidSku(sku),
          ean: product.ean,
          quantity: 0,
          method: null,
          product_name: product.productName!,
          product_price: product.productPrice!,
        });
        return {
          ...state,
          productsMap: updatedProductsMap,
          products: updatedProducts,
          totalPrice: newTotalPrice,
        };
      }
      const newQuantity = product.quantity - 1;
      const updatedProductsMap = { ...product, quantity: newQuantity };
      trackBagProgress("bagProgressed", {
        action: TGTGBagProgressAction.ProductQuantityUpdated,
        bag_id: state.bagId!,
        product_sku: getValidSku(sku),
        ean: product.ean,
        quantity: newQuantity,
        method: null,
        product_name: product.productName!,
        product_price: product.productPrice!,
      });
      return {
        ...state,
        productsMap: { ...state.productsMap, [sku]: updatedProductsMap },
        totalPrice: newTotalPrice,
      };
    }),
  reset: () => set(initialState),
}));
